import { useMemo } from "react";
import { bayRules, closedIntervals } from "@/lib/availability";
import { expandPatternOccurrences } from "@/components/PatternClashPreview";
import { decomposeInSiteTz } from "@/lib/datetime";

// Snap to the top of the hour in the site's timezone (handles :30 offsets like Adelaide).
export function snapToHour(d, tz) {
  const x = new Date(d);
  x.setSeconds(0, 0);
  const minute = tz ? decomposeInSiteTz(x.toISOString(), tz).minute : x.getMinutes();
  return new Date(x.getTime() - minute * 60000);
}

const groupBy = (list, key) => {
  const m = new Map();
  for (const item of list) {
    const bucket = m.get(item[key]) || [];
    bucket.push(item);
    m.set(item[key], bucket);
  }
  return m;
};

// Ghost occurrences for patterns, minus ones already materialised as real bookings.
function patternGhosts(patterns, bookings, windowStart, windowEnd) {
  const m = new Map();
  const realKeys = new Set(
    bookings
      .filter((b) => b.recurring_pattern_id)
      .map((b) => `${b.recurring_pattern_id}|${new Date(b.scheduled_start).getTime()}`)
  );
  for (const p of patterns) {
    const occs = expandPatternOccurrences(p, windowStart, windowEnd);
    if (!occs.length) continue;
    const list = m.get(p.bay_id) || [];
    occs.forEach((o, i) => {
      if (realKeys.has(`${p.id}|${o.start.getTime()}`)) return;
      list.push({ id: `${p.id}-${i}`, pattern: p, start: o.start, end: o.end });
    });
    m.set(p.bay_id, list);
  }
  return m;
}

// Per-bay buckets + hour ticks derived from the loaded window data.
export function useGridModel({ data, siteId, windowStart, windowEnd, horizonHours }) {
  const { bookings, blackouts, patterns, availInputs } = data;

  const hourTicks = useMemo(
    () => Array.from({ length: horizonHours + 1 }, (_, h) => ({
      idx: h, date: new Date(windowStart.getTime() + h * 3600 * 1000),
    })),
    [windowStart, horizonHours]
  );

  const bookingsByBay = useMemo(() => groupBy(bookings, "bay_id"), [bookings]);

  const closedByBay = useMemo(() => {
    const m = new Map();
    if (!availInputs?.site || availInputs.site.id !== siteId) return m;
    for (const bay of availInputs.bays) {
      m.set(bay.id, closedIntervals(windowStart.getTime(), windowEnd.getTime(), bayRules(availInputs.site, bay)));
    }
    return m;
  }, [availInputs, siteId, windowStart, windowEnd]);

  const blackoutsByBay = useMemo(() => ({
    byBay: groupBy(blackouts.filter((b) => b.bay_id), "bay_id"),
    siteWide: blackouts.filter((b) => !b.bay_id),
  }), [blackouts]);

  const patternGhostsByBay = useMemo(
    () => patternGhosts(patterns, bookings, windowStart, windowEnd),
    [patterns, bookings, windowStart, windowEnd]
  );

  return { hourTicks, bookingsByBay, closedByBay, blackoutsByBay, patternGhostsByBay };
}
